import { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronRight, CheckCircle2, AlertTriangle, Flame, Zap, Thermometer, Info, MapPin } from 'lucide-react';
import getApiUrl from '../../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const TYPES_CHAUFFAGE = [
  { id: 'fioul', label: 'Chaudi\u00e8re fioul' },
  { id: 'gaz', label: 'Chaudi\u00e8re gaz' },
  { id: 'electrique', label: '\u00c9lectrique (radiateurs/convecteurs)' },
  { id: 'bois', label: 'Bois / Granul\u00e9s' },
  { id: 'autre', label: 'Autre / Ne sait pas' }
];

const PRODUITS_DEFAUT = [
  { id: 'pac_air_eau', label: 'PAC Air/Eau', cat: 'chauffage',
    condition: 'chauffage_fossile', chauffagesOk: ['gaz', 'fioul'],
    maisonRequise: true,
    info: 'Sortie fossile uniquement (gaz/fioul). CEE via grille BAR-TH-171 (zone + surface + Etas).' },
  { id: 'pac_air_air', label: 'PAC Air/Air (CEE uniquement)', cat: 'chauffage',
    condition: null, copro: true,
    info: 'Aucune aide MPR. Prime CEE forfaitaire.' },
  { id: 'poele_granules', label: 'Po\u00eale \u00e0 granul\u00e9s / insert', cat: 'chauffage',
    condition: null, maisonRequise: true,
    info: 'MPR monogeste 2026. CEE Coup de Pouce Chauffage.' },
  { id: 'chaudiere_biomasse', label: 'Chaudi\u00e8re biomasse', cat: 'chauffage',
    condition: null, maisonRequise: true, exclueMonogeste2026: true,
    info: 'Exclue MPR monogeste depuis 01/2026. CEE forfaitaire.' },
  { id: 'iso_combles', label: 'Isolation combles (rampants)', cat: 'isolation',
    condition: 'iso_combles', surface: true, maisonRequise: true,
    info: 'Rampants/plafonds R>=6. CEE au m\u00b2.' },
  { id: 'iso_murs_ite', label: 'Isolation murs (ITE)', cat: 'isolation',
    condition: 'iso_murs', surface: true, exclueMonogeste2026: true, copro: true,
    info: 'Exclue MPR monogeste 01/2026. CEE au m\u00b2.' },
  { id: 'iso_murs_iti', label: 'Isolation murs (ITI)', cat: 'isolation',
    condition: 'iso_murs', surface: true, exclueMonogeste2026: true,
    info: 'Exclue MPR monogeste 01/2026. CEE au m\u00b2.' },
  { id: 'iso_planchers', label: 'Isolation planchers bas', cat: 'isolation',
    condition: 'iso_planchers', surface: true, maisonRequise: true, exclueMonogeste2026: true,
    info: 'Exclu MPR monogeste 01/2026. CEE BAR-EN-103 au m\u00b2.' },
  { id: 'chauffe_eau_thermo', label: 'Chauffe-eau thermodynamique', cat: 'eau_chaude',
    condition: null,
    info: '\u00c9ligible MPR par geste 2026. CEE forfaitaire.' },
  { id: 'chauffe_eau_solaire', label: 'Chauffe-eau solaire', cat: 'eau_chaude',
    condition: null, maisonRequise: true,
    info: '\u00c9ligible MPR par geste 2026. CEE forfaitaire.' },
  { id: 'vmc_double_flux', label: 'VMC double flux', cat: 'ventilation',
    condition: null,
    info: '\u00c9ligible MPR par geste 2026. CEE forfaitaire.' },
  { id: 'fenetres', label: 'Fen\u00eatres / double vitrage', cat: 'isolation',
    condition: 'fenetres_simple_vitrage', nbFenetres: true,
    info: 'Remplacement simple vitrage. Uw<=1.3. +15 ans.' },
];

const COULEURS_MPR = {
  bleu: { label: 'Bleu (Tr\u00e8s modeste)', color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/30' },
  jaune: { label: 'Jaune (Modeste)', color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500/30' },
  violet: { label: 'Violet (Interm\u00e9diaire)', color: 'text-violet-400', bg: 'bg-violet-500/20', border: 'border-violet-500/30' },
  rose: { label: 'Rose (Sup\u00e9rieur)', color: 'text-pink-400', bg: 'bg-pink-500/20', border: 'border-pink-500/30' }
};

const CAT_LABELS = { chauffage: 'Chauffage', isolation: 'Isolation', eau_chaude: 'Eau chaude', ventilation: 'Ventilation' };

// Zone climatique depuis code postal
const getZoneClimatique = (codePostal, zonesMap) => {
  if (!codePostal || !zonesMap) return null;
  const cp = String(codePostal).trim();
  let dept = cp.substring(0, 2);
  if (dept === '20') {
    const cp3 = parseInt(cp.substring(0, 3));
    dept = cp3 >= 201 && cp3 <= 204 ? '2A' : '2B';
  }
  for (const [zone, depts] of Object.entries(zonesMap)) {
    if (depts.includes(dept)) return zone;
  }
  return 'H1'; // defaut si non trouve
};

// Tranche surface PAC
const getTrancheSurfacePAC = (surface, typeBien) => {
  const s = parseFloat(surface) || 0;
  if (typeBien === 'maison') {
    if (s < 70) return 's70';
    if (s < 90) return 's70_90';
    return 's90';
  }
  // appartement
  if (s < 35) return 's35';
  if (s < 60) return 's35_60';
  return 's60';
};

const SimulateurRenoEnergieWidget = ({ mpaData, onDataChange, initialData = null, collapsed = true, readOnly = false }) => {
  const [ouvert, setOuvert] = useState(!collapsed);
  const [tarifs, setTarifs] = useState(null);
  const [codePostalLocal, setCodePostalLocal] = useState('');
  const [data, setData] = useState({
    anciennete: '',
    chauffageActuel: '',
    surfaceLogement: '',
    isolation: {
      combles: { etat: '', surface: '' },
      murs: { etat: '', surface: '' },
      planchers: { etat: '', surface: '' }
    },
    nbFenetres: '',
    produitsSelectionnes: [],
    categorieMPR: null,
    checked: false
  });

  // Restore from initialData on mount (une seule fois)
  const initialDataApplied = useRef(false);
  useEffect(() => {
    if (initialData && typeof initialData === 'object' && !initialDataApplied.current) {
      initialDataApplied.current = true;
      setData(prev => ({ ...prev, ...initialData }));
    }
  }, []);


  // Sync code postal depuis mpaData (étape 1)
  useEffect(() => {
    const cp = mpaData?.codePostal || mpaData?.code_postal || '';
    if (cp && !codePostalLocal) setCodePostalLocal(cp);
  }, [mpaData?.codePostal, mpaData?.code_postal]);

  useEffect(() => {
    authFetch(`${API}/admin/tarifs-reno`)
      .then(r => r.json())
      .then(d => setTarifs(d))
      .catch(() => setTarifs(null));
  }, []);

  const prerequis = {
    estProprietaire: mpaData?.statutLogement === 'proprietaire',
    estResidencePrincipale: mpaData?.statutLogement !== 'proprietaire' || mpaData?.typeResidence === 'principale',
    estMaison: mpaData?.typeBien === 'maison',
    typeBienRenseigne: !!mpaData?.typeBien,
    statutRenseigne: !!mpaData?.statutLogement,
    anciennete15: data.anciennete === '15+',
    anciennete2a15: data.anciennete === '2-15',
    ancienneteRenseignee: !!data.anciennete && data.anciennete !== 'nsp',
    rfrRenseigne: !!mpaData?.rfr,
    chauffageFioul: data.chauffageActuel === 'fioul'
  };

  const prerequisBloquant =
    (prerequis.statutRenseigne && !prerequis.estResidencePrincipale) ||
    (prerequis.statutRenseigne && !prerequis.estProprietaire);

  const ancienneteValide = prerequis.anciennete15 || (prerequis.anciennete2a15 && prerequis.chauffageFioul);

  // Zone climatique depuis code postal local
  const zoneClimatique = useMemo(() => {
    return getZoneClimatique(codePostalLocal, tarifs?.zones_climatiques);
  }, [codePostalLocal, tarifs?.zones_climatiques]);

  // Etas par defaut depuis config
  const etasDefaut = tarifs?.cee_params?.etas_defaut || 140;

  const calculerCategorieMPR = () => {
    const zone = mpaData?.zone === 'idf' ? 'idf' : 'province';
    const nb = String(Math.min(Math.max(mpaData?.nbPersonnes || 1, 1), 5));
    const rfr = parseInt(mpaData?.rfr) || 0;
    if (!rfr) return null;
    const plafonds = tarifs?.plafonds_mpr_2026?.[zone]?.[nb];
    if (!plafonds) return null;
    if (rfr <= plafonds.bleu) return 'bleu';
    if (rfr <= plafonds.jaune) return 'jaune';
    if (rfr <= plafonds.violet) return 'violet';
    return 'rose';
  };

  const estEligible = (produit) => {
    if (prerequisBloquant) return false;
    if (!ancienneteValide && !prerequis.anciennete2a15) return false;
    if (prerequis.anciennete2a15 && !prerequis.chauffageFioul) return false;
    if (produit.maisonRequise && prerequis.typeBienRenseigne && !prerequis.estMaison) return false;
    if (produit.exclueMonogeste2026) return false;
    if (!produit.condition) return true;
    if (produit.condition === 'chauffage_fossile') return produit.chauffagesOk?.includes(data.chauffageActuel);
    if (produit.condition === 'iso_combles') return data.isolation.combles.etat === 'non';
    if (produit.condition === 'iso_murs') return data.isolation.murs.etat === 'non';
    if (produit.condition === 'iso_planchers') return data.isolation.planchers.etat === 'non';
    if (produit.condition === 'fenetres_simple_vitrage') return true;
    return true;
  };

  const estEligibleCEE = (produit) => {
    if (produit.maisonRequise && prerequis.typeBienRenseigne && !prerequis.estMaison) return false;
    if (produit.condition === 'iso_combles') return data.isolation.combles.etat === 'non';
    if (produit.condition === 'iso_murs') return data.isolation.murs.etat === 'non';
    if (produit.condition === 'iso_planchers') return data.isolation.planchers.etat === 'non';
    return true;
  };

  const raisonNonEligible = (produit) => {
    if (!prerequis.estProprietaire && prerequis.statutRenseigne) return 'Propri\u00e9taire occupant requis (MPR)';
    if (prerequisBloquant) return 'R\u00e9sidence principale requise (MPR)';
    if (!ancienneteValide) {
      if (prerequis.anciennete2a15 && !prerequis.chauffageFioul)
        return 'Logement 2-15 ans : MPR uniquement si sortie fioul';
      return 'Logement +15 ans requis (MPR m\u00e9tropole)';
    }
    if (produit.exclueMonogeste2026) return 'Exclu du monogeste MPR depuis 01/2026';
    if (produit.maisonRequise && prerequis.typeBienRenseigne && !prerequis.estMaison) return 'Maison individuelle requise';
    if (produit.condition === 'chauffage_fossile') {
      if (!data.chauffageActuel) return 'Renseignez le chauffage actuel';
      if (data.chauffageActuel === 'electrique') return 'Chauffage \u00e9lectrique = pas de MPR';
      if (data.chauffageActuel === 'bois') return 'D\u00e9j\u00e0 en EnR (bois) \u2192 pas de MPR';
      return 'Chauffage fossile requis (gaz/fioul)';
    }
    if (produit.condition?.startsWith('iso_')) return 'Zone d\u00e9j\u00e0 isol\u00e9e ou non renseign\u00e9e';
    return '';
  };

  // === CALCUL CEE ===
  const calculerCEE_PAC = (cat) => {
    if (!tarifs?.cee_pac_grille || !zoneClimatique) return 0;
    const typeBien = mpaData?.typeBien === 'maison' ? 'maison' : 'appartement';
    const grille = tarifs.cee_pac_grille[typeBien]?.[zoneClimatique];
    if (!grille) return 0;

    const precaire = (cat === 'bleu' || cat === 'jaune') ? 'precaire' : 'classique';
    const tranche = getTrancheSurfacePAC(data.surfaceLogement, typeBien);
    const etas = etasDefaut;
    const etasKey = etas >= 140 ? 'etas_140' : 'etas_111';

    return grille[precaire]?.[tranche]?.[etasKey] || 0;
  };

  const getEstimation = (produit) => {
    const cat = data.categorieMPR;
    if (!cat || !tarifs?.produits?.[produit.id]) return null;

    const tp = tarifs.produits[produit.id];
    const eligible = estEligible(produit);
    const eligibleCEE = estEligibleCEE(produit);
    const ceeMode = tp.cee_mode || 'forfait';

    let mpr = 0;
    let cee = 0;

    // MPR
    if (eligible && cat !== 'rose' && !produit.exclueMonogeste2026) {
      mpr = tp.mpr?.[cat] || 0;
    }

    // CEE
    if (eligibleCEE) {
      if (ceeMode === 'grille_pac') {
        cee = calculerCEE_PAC(cat);
      } else if (ceeMode === 'surface') {
        const zone = produit.id === 'iso_combles' ? 'combles'
          : produit.id.includes('murs') ? 'murs' : 'planchers';
        const surface = parseFloat(data.isolation[zone]?.surface) || 0;
        const ceeM2 = tp.cee_euro_m2?.[cat] || 0;
        cee = surface * ceeM2;
        if (surface > 0 && eligible) mpr = mpr * surface;
      } else if (ceeMode === 'fenetre') {
        const nb = parseInt(data.nbFenetres) || 0;
        const ceeFen = tp.cee_euro_fenetre?.[cat] || 0;
        cee = nb * ceeFen;
        if (nb > 0 && eligible) mpr = mpr * nb;
      } else {
        // forfait
        cee = tp.cee_forfaits?.[cat] || 0;
      }
    }

    // Plafond cumul CEE + MPR
    const plafond = tarifs?.cee_params?.plafond_cumul_cee_mpr;
    const totalBrut = mpr + cee;
    let plafonne = false;
    if (plafond && totalBrut > plafond && produit.id === 'pac_air_eau') {
      const exces = totalBrut - plafond;
      mpr = Math.max(0, mpr - exces);
      plafonne = true;
    }

    return { mpr: Math.round(mpr), cee: Math.round(cee), total: Math.round(mpr + cee), eligible, eligibleCEE, plafonne };
  };

  const toggleProduit = (id) => {
    if (readOnly) return;
    setData(prev => ({
      ...prev,
      produitsSelectionnes: prev.produitsSelectionnes.includes(id)
        ? prev.produitsSelectionnes.filter(p => p !== id)
        : [...prev.produitsSelectionnes, id]
    }));
  };

  const testerEligibilite = () => {
    setData(prev => ({ ...prev, categorieMPR: calculerCategorieMPR(), checked: true }));
  };

  // Sync zoneClimatique et codePostal dans data pour propagation au backend
  useEffect(() => {
    if (zoneClimatique || codePostalLocal) {
      setData(prev => {
        const updates = {};
        if (zoneClimatique && prev.zoneClimatique !== zoneClimatique) updates.zoneClimatique = zoneClimatique;
        if (codePostalLocal && prev.codePostal !== codePostalLocal) updates.codePostal = codePostalLocal;
        return Object.keys(updates).length ? { ...prev, ...updates } : prev;
      });
    }
  }, [zoneClimatique, codePostalLocal]);

  useEffect(() => { if (onDataChange) onDataChange(data); }, [data]);

  const totalEstime = data.checked && data.categorieMPR
    ? data.produitsSelectionnes.reduce((acc, id) => {
        const produit = PRODUITS_DEFAUT.find(p => p.id === id);
        if (!produit) return acc;
        const est = getEstimation(produit);
        if (!est) return acc;
        return { mpr: acc.mpr + est.mpr, cee: acc.cee + est.cee, total: acc.total + est.total };
      }, { mpr: 0, cee: 0, total: 0 })
    : null;

  const catInfo = data.categorieMPR ? COULEURS_MPR[data.categorieMPR] : null;

  return (
    <div data-testid="reno-energie-widget" className="bg-gradient-to-r from-orange-900/30 to-amber-900/30 border border-orange-500/30 rounded-xl p-4">
      <button onClick={() => setOuvert(!ouvert)} className="w-full flex items-center justify-between text-left" data-testid="reno-energie-toggle">
        <div className="flex items-center gap-3">
          <Flame className="w-5 h-5 text-orange-400" />
          <div>
            <h3 className="text-white font-bold">R&eacute;novation &Eacute;nerg&eacute;tique</h3>
            <p className="text-xs text-slate-400">MaPrimeR&eacute;nov' + CEE BAR-TH-171</p>
          </div>
        </div>
        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${ouvert ? 'rotate-90' : ''}`} />
      </button>

      {ouvert && (
        <div className="mt-4 space-y-4">
          {/* Pr&eacute;-requis */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Anciennet&eacute; du logement</label>
              <select value={data.anciennete} disabled={readOnly}
                onChange={e => setData(prev => ({ ...prev, anciennete: e.target.value }))}
                data-testid="select-anciennete"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm">
                <option value="">S&eacute;lectionner</option>
                <option value="15+">+ de 15 ans</option>
                <option value="2-15">2 &agrave; 15 ans</option>
                <option value="-2">- de 2 ans</option>
                <option value="nsp">Ne sait pas</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">+15 ans requis MPR m&eacute;tropole</p>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Chauffage actuel</label>
              <select value={data.chauffageActuel} disabled={readOnly}
                onChange={e => setData(prev => ({ ...prev, chauffageActuel: e.target.value }))}
                data-testid="select-chauffage"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm">
                <option value="">S&eacute;lectionner</option>
                {TYPES_CHAUFFAGE.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Surface logement (m&sup2;)</label>
              <input type="number" value={data.surfaceLogement} disabled={readOnly}
                onChange={e => setData(prev => ({ ...prev, surfaceLogement: e.target.value }))}
                data-testid="input-surface"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                placeholder="Ex: 110" min="10" max="500" />
              <p className="text-xs text-slate-500 mt-1">Impact CEE PAC (zone + surface)</p>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Code postal</label>
              <input type="text" value={codePostalLocal} disabled={readOnly}
                onChange={e => { setCodePostalLocal(e.target.value); setData(prev => ({ ...prev, checked: false })); }}
                data-testid="input-code-postal-reno"
                maxLength={5}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                placeholder="Ex: 75015" />
              {codePostalLocal.length >= 2 && zoneClimatique && (
                <p className="text-xs mt-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-blue-400" />
                  <span className="text-blue-400 font-medium">Zone {zoneClimatique}</span>
                  <span className="text-slate-500">| Etas {etasDefaut}%{etasDefaut >= 140 ? ' (major\u00e9)' : ''}</span>
                </p>
              )}
              {(!codePostalLocal || codePostalLocal.length < 2) && (
                <p className="text-xs text-amber-400 mt-1">Requis pour calcul CEE PAC</p>
              )}
            </div>
          </div>

          {/* Isolation */}
          <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-3">
            <h4 className="text-sm text-white font-medium mb-2">
              <Thermometer className="w-4 h-4 inline mr-1" />&Eacute;tat isolation actuel</h4>
            <div className="grid grid-cols-3 gap-3">
              {[
                { key: 'combles', label: 'Combles / toiture' },
                { key: 'murs', label: 'Murs ext&eacute;rieurs' },
                { key: 'planchers', label: 'Planchers bas' }
              ].map(iso => (
                <div key={iso.key}>
                  <label className="block text-xs text-slate-400 mb-1" dangerouslySetInnerHTML={{ __html: iso.label }} />
                  <select disabled={readOnly}
                    value={data.isolation[iso.key].etat}
                    onChange={e => setData(prev => ({
                      ...prev, isolation: { ...prev.isolation, [iso.key]: { ...prev.isolation[iso.key], etat: e.target.value } }
                    }))}
                    className="w-full px-2 py-1.5 bg-slate-900 border border-slate-600 rounded text-white text-xs">
                    <option value="">Non renseign&eacute;</option>
                    <option value="oui">Oui, isol&eacute;</option>
                    <option value="non">Non, pas isol&eacute;</option>
                    <option value="nsp">Ne sait pas</option>
                  </select>
                  {data.isolation[iso.key].etat === 'non' && (
                    <input type="number" placeholder="Surface m&sup2;"
                      value={data.isolation[iso.key].surface} disabled={readOnly}
                      onChange={e => setData(prev => ({
                        ...prev, isolation: { ...prev.isolation, [iso.key]: { ...prev.isolation[iso.key], surface: e.target.value } }
                      }))}
                      className="w-full mt-1 px-2 py-1 bg-slate-900 border border-slate-600 rounded text-white text-xs" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Simple vitrage */}
          <div className="flex items-center gap-3">
            <label className="text-sm text-slate-400">Nombre de fen&ecirc;tres simple vitrage :</label>
            <input type="number" min="0" max="30" placeholder="0" disabled={readOnly}
              value={data.nbFenetres}
              onChange={e => setData(prev => ({ ...prev, nbFenetres: e.target.value }))}
              data-testid="input-fenetres"
              className="w-20 px-3 py-1.5 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm text-center" />
          </div>

          {/* Tester */}
          {mpaData?.rfr && !data.checked && (
            <button onClick={testerEligibilite} data-testid="btn-tester-eligibilite"
              className="w-full py-3 bg-orange-600 hover:bg-orange-500 text-white rounded-lg font-bold flex items-center justify-center gap-2">
              <Zap className="w-5 h-5" />Tester l'&eacute;ligibilit&eacute; R&eacute;no &Eacute;nergie
            </button>
          )}
          {!mpaData?.rfr && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm text-amber-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              Compl&eacute;tez d'abord le RFR dans l'&eacute;tape &Eacute;ligibilit&eacute; MPA
            </div>
          )}

          {/* R&eacute;sultat cat&eacute;gorie */}
          {data.checked && catInfo && (
            <div className={`p-3 rounded-lg border ${catInfo.bg} ${catInfo.border}`} data-testid="categorie-mpr-result">
              <div className="flex items-center gap-2">
                <CheckCircle2 className={`w-5 h-5 ${catInfo.color}`} />
                <span className={`font-bold ${catInfo.color}`}>Cat&eacute;gorie {catInfo.label}</span>
                {zoneClimatique && <span className="text-xs text-slate-400 ml-2">| Zone {zoneClimatique}</span>}
                {data.surfaceLogement && <span className="text-xs text-slate-400">| {data.surfaceLogement} m&sup2;</span>}
                <span className="text-xs text-slate-400">| Etas {etasDefaut}%</span>
              </div>
            </div>
          )}
          {data.checked && !data.categorieMPR && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
              Impossible de d&eacute;terminer la cat&eacute;gorie MPR (v&eacute;rifiez RFR, zone et nb personnes)
            </div>
          )}

          {/* Produits */}
          {data.checked && data.categorieMPR && (
            <div className="space-y-3">
              {Object.entries(CAT_LABELS).map(([catKey, catLabel]) => {
                const produits = PRODUITS_DEFAUT.filter(p => p.cat === catKey);
                if (!produits.length) return null;
                return (
                  <div key={catKey}>
                    <h4 className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">{catLabel}</h4>
                    <div className="space-y-1.5">
                      {produits.map(produit => {
                        const eligible = estEligible(produit);
                        const eligibleCEE = estEligibleCEE(produit);
                        const estimation = getEstimation(produit);
                        const selected = data.produitsSelectionnes.includes(produit.id);
                        const canSelect = eligible || eligibleCEE;
                        const raison = !eligible ? raisonNonEligible(produit) : '';

                        return (
                          <div key={produit.id}
                            onClick={() => canSelect && toggleProduit(produit.id)}
                            data-testid={`produit-${produit.id}`}
                            className={`p-3 rounded-lg border transition-all ${
                              !canSelect ? 'opacity-40 border-slate-700 bg-slate-800/20 cursor-not-allowed'
                              : selected ? 'border-orange-500 bg-orange-500/10 cursor-pointer ring-1 ring-orange-500/50'
                              : 'border-slate-700 bg-slate-800/30 cursor-pointer hover:border-orange-500/50'
                            }`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                                  selected ? 'bg-orange-500 border-orange-500' : 'border-slate-600'
                                }`}>
                                  {selected && <CheckCircle2 className="w-3 h-3 text-white" />}
                                </div>
                                <span className="text-sm text-white font-medium truncate">{produit.label}</span>
                                {produit.exclueMonogeste2026 && (
                                  <span className="text-xs px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded shrink-0">CEE seul</span>
                                )}
                                {!eligible && eligibleCEE && !produit.exclueMonogeste2026 && (
                                  <span className="text-xs px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded shrink-0">CEE uniquement</span>
                                )}
                              </div>
                              {estimation && canSelect && (
                                <div className="flex items-center gap-3 shrink-0 text-right">
                                  {estimation.mpr > 0 && (
                                    <div className="text-xs">
                                      <span className="text-slate-500">MPR </span>
                                      <span className="text-emerald-400 font-bold">{estimation.mpr.toLocaleString()}&euro;</span>
                                    </div>
                                  )}
                                  {estimation.cee > 0 && (
                                    <div className="text-xs">
                                      <span className="text-slate-500">CEE </span>
                                      <span className="text-blue-400 font-bold">{estimation.cee.toLocaleString()}&euro;</span>
                                    </div>
                                  )}
                                  {estimation.total > 0 && (
                                    <div className="text-xs">
                                      <span className="text-orange-400 font-bold">{estimation.total.toLocaleString()}&euro;</span>
                                    </div>
                                  )}
                                  {estimation.plafonne && (
                                    <span className="text-xs text-red-400" title="Plafond cumul CEE+MPR atteint">&#9888;</span>
                                  )}
                                </div>
                              )}
                            </div>
                            {raison && <p className="text-xs text-red-400/70 mt-1 ml-6">{raison}</p>}
                            <p className="text-xs text-slate-500 mt-1 ml-6 flex items-center gap-1">
                              <Info className="w-3 h-3" />{produit.info}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Total */}
          {totalEstime && totalEstime.total > 0 && (
            <div className="p-4 bg-gradient-to-r from-orange-500/20 to-amber-500/20 border border-orange-500/50 rounded-xl" data-testid="total-estime">
              <h4 className="text-white font-bold mb-2">Estimation totale aides</h4>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <span className="text-xs text-slate-400 block">MPR</span>
                  <span className="text-lg font-bold text-emerald-400">{totalEstime.mpr.toLocaleString()}&euro;</span>
                </div>
                <div>
                  <span className="text-xs text-slate-400 block">CEE</span>
                  <span className="text-lg font-bold text-blue-400">{totalEstime.cee.toLocaleString()}&euro;</span>
                </div>
                <div>
                  <span className="text-xs text-slate-400 block">Total</span>
                  <span className="text-lg font-bold text-orange-400">{totalEstime.total.toLocaleString()}&euro;</span>
                </div>
              </div>
              {data.produitsSelectionnes.includes('pac_air_eau') && (
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Plafond cumul CEE+MPR PAC : {(tarifs?.cee_params?.plafond_cumul_cee_mpr || 12000).toLocaleString()}&euro;
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SimulateurRenoEnergieWidget;
